
import {
  createContext,
  useContext,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from "react";

export type CartItem = {
  productId: string;
  slug: string;
  name: string;
  brand: string;
  price: number;
  imageUrl: string | null;
  quantity: number;
};

export type AppliedCoupon = {
  code: string;
  type: "percentage" | "fixed";
  value: number;
  description: string;
};

type CartState = {
  items: CartItem[];
  coupon: AppliedCoupon | null;
};

type CartContextValue = {
  items: CartItem[];
  coupon: AppliedCoupon | null;
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  applyCoupon: (coupon: AppliedCoupon) => void;
  removeCoupon: () => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
  discount: number;
  totalPrice: number;
};

const STORAGE_KEY = "aflt-cart";

let state: CartState = { items: [], coupon: null };
let listeners: Array<() => void> = [];

function emitChange() {
  for (const listener of listeners) listener();
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function hydrate() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.items && Array.isArray(parsed.items)) {
        state = { items: parsed.items, coupon: parsed.coupon ?? null };
        emitChange();
      }
    }
  } catch {}
}

function subscribe(listener: () => void) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot() {
  return state;
}

function getServerSnapshot(): CartState {
  return { items: [], coupon: null };
}

function addItem(item: Omit<CartItem, "quantity">) {
  const existing = state.items.find((i) => i.productId === item.productId);
  if (existing) {
    state = {
      ...state,
      items: state.items.map((i) =>
        i.productId === item.productId
          ? { ...i, quantity: i.quantity + 1 }
          : i,
      ),
    };
  } else {
    state = { ...state, items: [...state.items, { ...item, quantity: 1 }] };
  }
  persist();
  emitChange();
}

function removeItem(productId: string) {
  state = { ...state, items: state.items.filter((i) => i.productId !== productId) };
  persist();
  emitChange();
}

function updateQuantity(productId: string, quantity: number) {
  if (quantity <= 0) {
    removeItem(productId);
    return;
  }
  state = {
    ...state,
    items: state.items.map((i) =>
      i.productId === productId ? { ...i, quantity } : i,
    ),
  };
  persist();
  emitChange();
}

function applyCoupon(coupon: AppliedCoupon) {
  state = { ...state, coupon };
  persist();
  emitChange();
}

function removeCoupon() {
  state = { ...state, coupon: null };
  persist();
  emitChange();
}

function clearCart() {
  state = { items: [], coupon: null };
  persist();
  emitChange();
}

function calcDiscount(subtotal: number, coupon: AppliedCoupon | null): number {
  if (!coupon) return 0;
  if (coupon.type === "percentage") {
    return Math.round(subtotal * (coupon.value / 100));
  }
  return Math.min(coupon.value, subtotal);
}

const CartContext = createContext<CartContextValue | null>(null);

let hydrated = false;

export function CartProvider({ children }: { children: ReactNode }) {
  const cart = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  if (typeof window !== "undefined" && !hydrated) {
    hydrated = true;
    hydrate();
  }

  const totalItems = cart.items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = cart.items.reduce(
    (sum, i) => sum + i.price * i.quantity,
    0,
  );
  const discount = calcDiscount(subtotal, cart.coupon);
  const totalPrice = Math.max(0, subtotal - discount);

  const value: CartContextValue = {
    items: cart.items,
    coupon: cart.coupon,
    addItem: useCallback(addItem, []),
    removeItem: useCallback(removeItem, []),
    updateQuantity: useCallback(updateQuantity, []),
    applyCoupon: useCallback(applyCoupon, []),
    removeCoupon: useCallback(removeCoupon, []),
    clearCart: useCallback(clearCart, []),
    totalItems,
    subtotal,
    discount,
    totalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
